import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChartPie, FaChartBar, FaChartLine, FaFilter, FaCheck, FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { format, subMonths } from 'date-fns';
import BarChartStructure from '../BarChartStructure';
import LineChartStructure from '../LineChartStructure';
import { CURRENCY_SYMBOLS } from '../../utils/currencySymbols';
import { CURRENCY_SYMBOLS_TEXT } from '../../utils/currencySymbols';

const CategorySpecific = ({ categories, selectedCategories, setSelectedCategories, selectedCurrency = 'GBP' }) => {
  const { currentUser } = useAuth();
  const [expenseData, setExpenseData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);
  
  // Get the currency symbol for the selected currency
  const currencySymbol = CURRENCY_SYMBOLS[selectedCurrency] || '£';
  const currencySymbolText = CURRENCY_SYMBOLS_TEXT[selectedCurrency] || 'GBP';
  
  // Function to handle select all categories
  const handleSelectAll = () => {
    setSelectedCategories([...categories]);
  };

  // Function to handle clear all selected categories
  const handleClearAll = () => {
    setSelectedCategories([]);
  };

  // Fetch expense data from Firestore
  useEffect(() => {
    const fetchExpenseData = async () => {
      if (!currentUser) return;
      
      try {
        setIsLoading(true);
        
        // Get date 12 months ago for filtering
        const twelveMonthsAgo = subMonths(new Date(), 12);
        const twelveMonthsAgoStr = format(twelveMonthsAgo, 'yyyy-MM-dd');
        
        // This query matches your Firestore security rules structure
        // The expenses are stored directly in the user's document at /expenses/{userId}
        const expensesRef = doc(db, 'expenses', currentUser.uid);
        const expensesDoc = await getDoc(expensesRef);
        
        let allExpenses = [];
        
        if (expensesDoc.exists()) {
          const userData = expensesDoc.data();
          if (userData && userData.items) {
            // Convert the object structure to array of expenses with dates
            Object.entries(userData.items).forEach(([date, dayExpenses]) => {
              // Only include expenses from last 12 months
              if (date >= twelveMonthsAgoStr) {
                dayExpenses.forEach(expense => {
                  allExpenses.push({
                    ...expense,
                    date: date
                  });
                });
              }
            });
          }
        }
        
        setExpenseData(allExpenses);
      } catch (error) {
        console.error('Error fetching expense data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchExpenseData();
  }, [currentUser]);

  // Calculate category totals for bar chart
  const barChartData = useMemo(() => {
    if (!expenseData.length || !categories.length) return [];
    
    // Get the categories to display
    const categoriesToShow = selectedCategories.length 
      ? selectedCategories 
      : categories.slice(0, 5);
    
    // Create an object to hold category totals
    const categoryTotals = {};
    
    // Initialize all categories with zero
    categoriesToShow.forEach(category => {
      categoryTotals[category] = 0;
    });
    
    // Sum up expenses by category, filtering by selectedCurrency
    expenseData.forEach(expense => {
      if (categoriesToShow.includes(expense.category) && 
          (!expense.currency || expense.currency === selectedCurrency)) {
        categoryTotals[expense.category] += parseFloat(expense.amount);
      }
    });
    
    // Convert to the format required by BarChartStructure
    return Object.entries(categoryTotals).map(([category, total]) => ({
      name: category,
      amount: total
    }));
  }, [expenseData, categories, selectedCategories, selectedCurrency]);

  // Calculate data for monthly trends line chart
  const lineChartData = useMemo(() => {
    if (!expenseData.length || !categories.length) return [];
    
    // Get categories to display
    const categoriesToShow = selectedCategories.length 
      ? selectedCategories 
      : categories.slice(0, 3);
    
    // Create monthly buckets for last 6 months
    const months = [];
    const monthlyData = {};
    
    // Initialize last 6 months
    for (let i = 0; i < 6; i++) {
      const monthDate = subMonths(new Date(), i);
      const monthKey = format(monthDate, 'MMM yyyy');
      months.unshift(monthKey); // Add to beginning to get chronological order
      
      // Initialize monthly data structure
      monthlyData[monthKey] = {};
      categoriesToShow.forEach(category => {
        monthlyData[monthKey][category] = 0;
      });
    }
    
    // Group expenses by month and category, filtering by selectedCurrency
    expenseData.forEach(expense => {
      if (categoriesToShow.includes(expense.category) && 
          (!expense.currency || expense.currency === selectedCurrency)) {
        const expenseDate = new Date(expense.date);
        const monthKey = format(expenseDate, 'MMM yyyy');
        
        // Only include expenses from the last 6 months
        if (months.includes(monthKey)) {
          monthlyData[monthKey][expense.category] += parseFloat(expense.amount);
        }
      }
    });
    
    // Convert to format needed for LineChartStructure
    return months.map(month => {
      const dataPoint = { name: month };
      categoriesToShow.forEach(category => {
        dataPoint[category] = monthlyData[month][category] || 0;
      });
      return dataPoint;
    });
  }, [expenseData, categories, selectedCategories, selectedCurrency]);

  // Calculate stats for the cards
  const statsData = useMemo(() => {
    if (!expenseData.length || !selectedCategories.length) {
      return {
        mostSpentCategory: selectedCategories.length ? selectedCategories[0] : "N/A",
        monthlyIncrease: 0,
        totalSpent: 0,
        avgMonthlySpend: 0
      };
    }
    
    // Create buckets for categories and monthly totals
    const categoryTotals = {};
    const monthlyTotals = {};
    const currentMonth = format(new Date(), 'yyyy-MM');
    
    selectedCategories.forEach(category => {
      categoryTotals[category] = 0;
    });
    
    // Sum expenses by category and month
    expenseData.forEach(expense => {
      if (selectedCategories.includes(expense.category) && 
          (!expense.currency || expense.currency === selectedCurrency)) {
        const amount = parseFloat(expense.amount);
        
        // Sum by category
        if (expense.category && categoryTotals.hasOwnProperty(expense.category)) {
          categoryTotals[expense.category] += amount;
        }
        
        // Sum by month
        const month = expense.date.substring(0, 7); // yyyy-MM
        if (!monthlyTotals[month]) {
          monthlyTotals[month] = 0;
        }
        monthlyTotals[month] += amount;
      }
    });
    
    // Calculate month-over-month change like in SmartInsight
    const months = Object.keys(monthlyTotals).sort();
    const currentMonthTotal = monthlyTotals[currentMonth] || 0;
    const lastMonth = months[months.length - 2];
    const lastMonthTotal = monthlyTotals[lastMonth] || 0;
    const monthOverMonthChange = lastMonthTotal ? 
      ((currentMonthTotal - lastMonthTotal) / lastMonthTotal) * 100 : 0;
    
    // Find category with highest total
    let highestTotal = 0;
    let mostSpentCategory = selectedCategories[0] || "N/A";
    
    Object.entries(categoryTotals).forEach(([category, total]) => {
      if (total > highestTotal) {
        highestTotal = total;
        mostSpentCategory = category;
      }
    });
    
    // Calculate average monthly spend
    const totalSpent = Object.values(categoryTotals).reduce((a, b) => a + b, 0);
    const avgMonthlySpend = totalSpent / 12;
    
    return {
      mostSpentCategory,
      monthlyIncrease: monthOverMonthChange, // Using the new calculation
      totalSpent,
      avgMonthlySpend
    };
  }, [expenseData, selectedCategories, selectedCurrency]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header with info tooltip */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <FaChartPie className="mr-2 text-indigo-600 dark:text-indigo-400" />
            Category Analysis - {currencySymbolText}
          </h3>
        </div>
        
        {/* Helper text when no categories are selected */}
        {selectedCategories.length === 0 && categories.length > 0 && (
          <p className="text-sm text-indigo-600 dark:text-indigo-400 italic font-medium">
            Select categories below to analyse your spending patterns
          </p>
        )}
      </div>
      
      {/* Category selection with improved UI */}
      {categories.length > 0 && (
        <motion.div 
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
          >
            <div className="flex items-center">
              <FaFilter className="mr-2 text-gray-500 dark:text-gray-400" />
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Filter Categories ({selectedCategories.length}/{categories.length})
              </h4>
            </div>
            <div className="flex space-x-2 items-center">
              {isFilterExpanded && (
                <motion.div 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }}
                  className="flex space-x-2"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectAll();
                    }}
                    className="px-3 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearAll();
                    }}
                    className="px-3 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    Clear
                  </button>
                </motion.div>
              )}
              {isFilterExpanded ? (
                <FaChevronUp className="text-gray-500 dark:text-gray-400 w-4 h-4" />
              ) : (
                <FaChevronDown className="text-gray-500 dark:text-gray-400 w-4 h-4" />
              )}
            </div>
          </div>
          
          <AnimatePresence>
            {isFilterExpanded && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap gap-2 mt-2 p-2">
                  {categories.map(category => (
                    <motion.button
                      key={category}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        if (selectedCategories.includes(category)) {
                          setSelectedCategories(selectedCategories.filter(c => c !== category));
                        } else {
                          setSelectedCategories([...selectedCategories, category]);
                        }
                      }}
                      className={`px-3 py-1.5 text-sm rounded-full transition-colors flex items-center ${
                        selectedCategories.includes(category)
                          ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200 ring-1 ring-indigo-300 dark:ring-indigo-700'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {selectedCategories.includes(category) && (
                        <FaCheck className="mr-1 w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                      )}
                      {category}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
      
      {/* Loading indicator */}
      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      ) : (
        <>
          {/* Stats cards row - updated without fixed/variable metrics */}
          {categories.length > 0 && (
            <motion.div 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-750 p-4 rounded-xl shadow-sm border border-amber-100 dark:border-gray-700">
                <div className="text-amber-500 dark:text-amber-400 mb-1 text-sm font-medium">Most Spent On</div>
                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{statsData.mostSpentCategory}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">highest spending category</div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-750 p-4 rounded-xl shadow-sm border border-blue-100 dark:border-gray-700">
                <div className="text-blue-500 dark:text-blue-400 mb-1 text-sm font-medium">Avg. Monthly Spend</div>
                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{currencySymbol}{statsData.avgMonthlySpend.toFixed(2)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">per month over 12 months</div>
              </div>
              
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-gray-800 dark:to-gray-750 p-4 rounded-xl shadow-sm border border-emerald-100 dark:border-gray-700">
                <div className="text-emerald-500 dark:text-emerald-400 mb-1 text-sm font-medium">Monthly Spending Trend</div>
                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  <div className="flex items-center">
                    <span className={`${
                      statsData.monthlyIncrease > 0 
                        ? 'text-red-500 dark:text-red-400' 
                        : statsData.monthlyIncrease < 0 
                          ? 'text-emerald-500 dark:text-emerald-400' 
                          : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {statsData.monthlyIncrease > 0 ? '↑' : statsData.monthlyIncrease < 0 ? '↓' : '→'}&nbsp;
                      {Math.abs(statsData.monthlyIncrease).toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">vs last month</div>
              </div>
            </motion.div>
          )}
          
          {/* Chart sections - updated without fixed/variable pie chart */}
          {categories.length > 0 ? (
            <>
              {selectedCategories.length > 0 ? (
                <>
                  <motion.div 
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-center mb-4">
                      <FaChartBar className="mr-2 text-purple-500 dark:text-purple-400" />
                      <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">
                        12-Month Category Totals
                      </h4>
                    </div>
                    <BarChartStructure 
                      data={barChartData} 
                      dataKeys={['amount']} 
                      title=""
                      xAxisLabel="Category"
                      yAxisLabel={`Total Spent (${currencySymbol})`}
                      barColor="#818CF8"
                      currencySymbol={currencySymbol}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
                      Total spending by category over the past 12 months
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mt-6"
                  >
                    <div className="flex items-center mb-4">
                      <FaChartLine className="mr-2 text-emerald-500 dark:text-emerald-400" />
                      <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Monthly Category Trends</h4>
                    </div>
                    <LineChartStructure 
                      data={lineChartData} 
                      dataKeys={selectedCategories} 
                      title=""
                      xAxisLabel="Month"
                      yAxisLabel={`Amount (${currencySymbol})`}
                      currencySymbol={currencySymbol}
                    />
                  </motion.div>
                </>
              ) : (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 text-center"
                >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                    <FaFilter className="w-8 h-8 text-indigo-300 dark:text-indigo-700" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Categories Selected</h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    Please select at least one category above to view your spending analysis and charts.
                  </p>
                </motion.div>
              )}
            </>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 text-center"
            >
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                <FaChartPie className="w-8 h-8 text-indigo-300 dark:text-indigo-700" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Categories Available</h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                You don't have any expense categories defined yet. Add categories in your Settings to start tracking and analyzing your spending by category.
              </p>
              <button 
                onClick={() => window.location.href = '/settings'} 
                className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
              >
                Go to Settings
              </button>
            </motion.div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default CategorySpecific;
